import type { ReactNode } from 'react';
import logo from '@/assets/nudent-logo.svg';
import artwork from '@/assets/signin-background.png';
import formBackdrop from '@/assets/bg-screens.png';

/**
 * The signed-out screen - the same layout as the admin console's
 * (web/admin/src/components/layout/AuthLayout.tsx), so both portals' sign-in
 * screens look alike. Only the copy differs, and it is passed in by each page.
 */
export function AuthLayout({
  badge,
  headline,
  blurb,
  children,
}: {
  /**
   * Which console this is - ADMIN, PRODUCTION - for staff, who can sign into
   * more than one. Left out here: a customer has only this portal.
   */
  badge?: string;
  headline: string;
  blurb: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(380px,44%)_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        {/* Weighted below centre so the tooth sits a little high, clear of the
            headline at the foot of the column. */}
        <img
          src={artwork}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 size-full object-cover object-[50%_65%]"
        />
        {/* The brand wash, then the fade the headline sits on - both defined
            once in the shared tokens. */}
        <div className="auth-artwork-overlay absolute inset-0" aria-hidden="true" />
        <div className="auth-artwork-scrim absolute inset-0" aria-hidden="true" />

        {/* No card: the logo at the top and the copy at the foot, so the
            artwork between them stays in view. */}
        <div className="relative flex min-h-dvh flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            {/* Knocked out to white: the mark is navy and would sink into the
                blue artwork, and a white plate behind it looked pasted on. */}
            <img src={logo} alt="Nudent" className="h-7 w-auto brightness-0 invert" />
            {badge && (
              <span className="rounded-md border border-auth-panel-border bg-auth-artwork-foreground/15 px-2.5 py-1 font-mono text-[11px] font-bold tracking-[0.08em] text-auth-artwork-foreground backdrop-blur-sm">
                {badge}
              </span>
            )}
          </div>

          <div className="max-w-105">
            <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px] text-balance text-auth-artwork-foreground">
              {headline}
            </h1>
            <p className="mt-3.5 text-[14px] leading-[1.65] text-pretty text-auth-artwork-muted">
              {blurb}
            </p>
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center overflow-hidden px-10 py-12">
        <img
          src={formBackdrop}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 min-h-screen"
        />
        <div className="relative w-full max-w-95">
          {/* The mark, for the narrow layout where the artwork column is gone. */}
          <img src={logo} alt="Nudent" className="mb-8 h-6.5 w-auto lg:hidden" />
          {children}
        </div>
      </div>
    </div>
  );
}
